// src/auth/authUtils.js
export const AUTH_TOKEN_KEY = "authToken";
export const USER_ROLES_KEY = "userRoles";
export const USER_STATUS_KEY = "userStatus";
export const AUTH_TOKEN_EXPIRES_AT_KEY = "authTokenExpiresAt";
export const AUTH_CHANGE_EVENT = "signalfly-auth-change";

function emitAuthChange() {
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
}

export function decodeJwtPayload(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

function getTokenExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  return payload?.exp ? payload.exp * 1000 : null;
}

function isTokenExpired(token) {
  const expiryMs = getTokenExpiryMs(token);
  return Boolean(expiryMs && Date.now() >= expiryMs);
}

function clearStoredAuthSession({ emit = true } = {}) {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(USER_ROLES_KEY);
  localStorage.removeItem(USER_STATUS_KEY);
  localStorage.removeItem(AUTH_TOKEN_EXPIRES_AT_KEY);
  sessionStorage.removeItem("previousPath");
  if (emit) emitAuthChange();
}

export const getAuthToken = () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY) || "";
  if (token && isTokenExpired(token)) {
    clearStoredAuthSession();
    return "";
  }
  return token;
};

export const getAuthUserKey = () => {
  const token = getAuthToken();
  const payload = token ? decodeJwtPayload(token) : null;
  return (
    payload?.sub ||
    payload?.email ||
    payload?.username ||
    payload?.userName ||
    payload?.publicUserId ||
    ""
  );
};

export const getAuthProfileFallback = () => {
  const token = getAuthToken();
  const payload = token ? decodeJwtPayload(token) : null;
  if (!payload) return null;

  return {
    email: payload.email || "",
    userId: payload.userId || payload.sub || "",
    publicUserId: payload.publicUserId || "",
    status: payload.status || getUserStatus(),
    plan: payload.plan || "NONE",
    subscriptionActive: Boolean(payload.subscriptionActive),
    subscriptionExpiry: payload.subscriptionExpiry || null,
  };
};

export const isAuthenticated = () => Boolean(getAuthToken());

export const getUserRoles = () => {
  if (!getAuthToken()) return [];
  try {
    return JSON.parse(localStorage.getItem(USER_ROLES_KEY)) || [];
  } catch {
    return [];
  }
};

export const getUserStatus = () => (getAuthToken() ? localStorage.getItem(USER_STATUS_KEY) || "" : "");

export const setAuthSession = ({ token, roles = [], status = "" }) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    const expiryMs = getTokenExpiryMs(token);
    if (expiryMs) {
      localStorage.setItem(AUTH_TOKEN_EXPIRES_AT_KEY, String(expiryMs));
    } else {
      localStorage.removeItem(AUTH_TOKEN_EXPIRES_AT_KEY);
    }
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_TOKEN_EXPIRES_AT_KEY);
  }

  localStorage.setItem(USER_ROLES_KEY, JSON.stringify(Array.isArray(roles) ? roles : []));

  if (status) {
    localStorage.setItem(USER_STATUS_KEY, status);
  } else {
    localStorage.removeItem(USER_STATUS_KEY);
  }

  console.debug("Auth session stored", {
    hasToken: Boolean(token),
    roles: Array.isArray(roles) ? roles : [],
    status,
  });
  emitAuthChange();
};

export const clearAuthSession = () => {
  clearStoredAuthSession();
  console.debug("Auth session cleared");
};

export const getAuthSnapshot = () => ({
  isLoggedIn: isAuthenticated(),
  roles: getUserRoles(),
  status: getUserStatus(),
});

export const subscribeToAuthChanges = (callback) => {
  const handleChange = () => {
    callback(getAuthSnapshot());
  };

  window.addEventListener(AUTH_CHANGE_EVENT, handleChange);
  window.addEventListener("storage", handleChange);

  return () => {
    window.removeEventListener(AUTH_CHANGE_EVENT, handleChange);
    window.removeEventListener("storage", handleChange);
  };
};

export const hasRole = (role) => {
  return getUserRoles().includes(role);
};

export const isAdmin = () => hasRole("ROLE_ADMIN");
export const isTrader = () => hasRole("ROLE_TRADE") || isAdmin();
export const isGuest = () => hasRole("ROLE_GUEST") || isTrader();
export const isViewer = () => hasRole("ROLE_VIEW") || isGuest();
